import React, { FC, useCallback, useState } from "react";
import clsx from "clsx";
import { ReactComponent as RequiredDocumentsInsideCircle } from "../../../../assets/svg/required-documents-inside-circle.svg";
import { ReactComponent as ExamInsideCircle } from "../../../../assets/svg/exam-inside-circle.svg";
import { ReactComponent as Branch } from "../../../../assets/svg/branch-location.svg";
import { ReactComponent as HousingType } from "../../../../assets/svg/housing-type.svg";
import { ReactComponent as ArrowRight } from "../../../../assets/svg/arrow-right.svg";
import { ReactComponent as Scholarship } from "../../../../assets/svg/scholarship.svg";
import { ReactComponent as OtherFees } from "../../../../assets/svg/other-fees.svg";
import { ReactComponent as Location } from "../../../../assets/svg/location-map.svg";
import { ReactComponent as Intakes } from "../../../../assets/svg/calendar-yellow.svg";
import { AdapterLink } from "../../../../components/Adopter";
import { Routes } from "../../../../constants/routes";
import { urlReplacer } from "../../../../utils";
import { IProgramCardFrontEndModel } from "../../types";
import { ProgramTypeName } from "../../constants";
import { ExpandedRequiredDocuments } from "../CardField/components/ExpandedRequiredDocuments";
import { ExpandedScholarship } from "../CardField/components/ExpandScholarship";
import { ExpandedBranch } from "../CardField/components/ExpandedBranch";
import { ExpandedExams } from "../CardField/components/ExpandedExams";
import { CardFieldType } from "../constants";
import { useProgramCardStyles } from "./useProgramCardStyles";
import { CardField } from "../../../../components/CardField/CardField";
import Masker from "../../../../components/Masker/Masker";

interface ProgramCardProps {
  program: IProgramCardFrontEndModel;
}

export const ProgramCard: FC<ProgramCardProps> = ({ program }) => {
  const classes = useProgramCardStyles();

  const [expandedCardFieldId, setExpandedCardFieldId] = useState(null);
  const expandHandler = useCallback((id) => {
    setExpandedCardFieldId((prev) => {
      return prev === id ? null : id;
    });
  }, []);

  const closeCard = () => setExpandedCardFieldId(null);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <img
          alt={"School Logo"}
          src={program.schoolImage}
          className={classes.schoolImage}
          width={60}
          height={60}
        />
        <div className={classes.headerInfoContainer}>
          <div className={classes.headerInfoSubContainer1}>
            <div className={classes.schoolName}>{program.schoolName}</div>
            <div className={classes.majorName}>
              {program.isHigher ? (
                program.majorName
              ) : (
                <>
                  {program.levelName}
                  {program.grade && (
                    <span className={classes.grade}>{program.grade}</span>
                  )}
                </>
              )}
            </div>
            <div className={classes.degreeName}>
              {program.isHigher ? (
                program.degreeName
              ) : (
                <>
                  School Type -{" "}
                  <span className={classes.bold}>{program.schoolTypeName}</span>
                </>
              )}
            </div>
          </div>
          <div className={classes.headerInfoSubContainer2}>
            <div className={classes.headerProgramTypeContainer}>
              <div
                className={clsx(
                  classes.headerProgramType,
                  program.programType === ProgramTypeName.ONLINE
                    ? classes.backgroundOnline
                    : classes.backgroundOnCampus,
                )}
              >
                {program.programType}
              </div>
            </div>
            <Masker
              className={classes.tuitionContainer}
              isMasked={program.isMasked}
            >
              {program.hasFeesSupport && (
                <span className={classes.tuition}>
                  {program.tuitionOtherFeesSumWithCurrency}
                </span>
              )}
              <span className={classes.tuitionWithScholarship}>
                {program.totalTuitionWithUnit}
              </span>
            </Masker>
            <div className={classes.durationContainer}>
              {program.programDuration}
            </div>
          </div>
        </div>
      </div>
      <div className={classes.content}>
        <CardField
          id={CardFieldType.Branch}
          Icon={Branch}
          title={"Branches"}
          PreviewContent={[program.branches[0].name]}
          ExpandedContent={() => <ExpandedBranch branches={program.branches} />}
          onExpand={expandHandler}
          onClickAway={closeCard}
          isExpanded={expandedCardFieldId === CardFieldType.Branch}
        />
        <CardField
          Icon={Intakes}
          title={"Intakes"}
          PreviewContent={program.allIntakes}
        />
        <CardField
          id={CardFieldType.RequiredDocuments}
          Icon={RequiredDocumentsInsideCircle}
          title={"Required Documents"}
          PreviewContent={program.capitalizedRequiredDocuments}
          ExpandedContent={() => (
            <ExpandedRequiredDocuments
              reqDocs={program.capitalizedRequiredDocuments}
            />
          )}
          onExpand={expandHandler}
          onClickAway={closeCard}
          isExpanded={expandedCardFieldId === CardFieldType.RequiredDocuments}
          isMasked={false}
        />
        {program.isHigher ? (
          <CardField
            id={CardFieldType.Exams}
            Icon={ExamInsideCircle}
            title={"Exams"}
            PreviewContent={program.previewExams}
            ExpandedContent={() => <ExpandedExams exams={program.exams} />}
            onExpand={expandHandler}
            onClickAway={closeCard}
            isExpanded={expandedCardFieldId === CardFieldType.Exams}
          />
        ) : (
          <CardField
            Icon={HousingType}
            title={"Housing Types"}
            PreviewContent={program.housingTypeNames}
          />
        )}
        <CardField
          Icon={OtherFees}
          title={"Other Fees"}
          PreviewContent={[program.otherFeesTotal]}
          isMasked={program.isMasked}
        />
        <CardField
          id={CardFieldType.Scholarship}
          Icon={Scholarship}
          title={"Scholarship"}
          PreviewContent={[program.scholarship]}
          ExpandedContent={() => (
            <ExpandedScholarship
              scholarship={program.scholarship}
              financialAid={program.financialAid}
            />
          )}
          onExpand={expandHandler}
          onClickAway={closeCard}
          isExpanded={expandedCardFieldId === CardFieldType.Scholarship}
          isMasked={program.isMasked}
        />
      </div>
      <div className={classes.footer}>
        <div className={classes.addressContainer}>
          <Location className={classes.locationIcon} />
          <span className={classes.address}>{program.schoolCountry}</span>
        </div>
        <div>
          <AdapterLink
            to={urlReplacer(Routes.VIEW_PROGRAM, {
              typeId: program.typeId,
              id: program.id,
            })}
            className={classes.link}
          >
            <span>View More &nbsp;</span>
            <ArrowRight className={classes.arrowRight} />
          </AdapterLink>
        </div>
      </div>
    </div>
  );
};
